import { ActKc2StatusEnum, ContractStatusEnum, CostGroupEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';

import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { WorkOrderFilterType } from '../WorkOrderFilter/types';
import { WorkOrderFilterLevelEnum } from '../WorkOrderFilter/types';
import { useWorkOrderActs } from '../WorkOrderTable/hooks';

enum TooltipComponentName {
    Other = 'Other',
    Uer = 'Uer',
    Coefficient = 'Coefficient',
}

export enum ModalName {
    Other = 'Other',
    Uer = 'Uer',
    Coefficient = 'Coefficient',
}

enum TooltipText {
    Other = 'В НЗ в статусе "В работе" добавление прочих доступно через создание Акта КС-2',
    Uer = 'В НЗ в статусе "В работе" добавление расценок доступно через создание Акта КС-2',
    Coefficient = 'В статусе "В работе" недоступно массовое применение коэффициентов',
}

interface IUseAddToWorkOrderMenu {
    filters: WorkOrderFilterType;
    isDisabledByProjectStatus: boolean;
    statusContract?: ContractStatusEnum;
    statusWorkOrder?: WorkOrderStatusEnum;
}

export const useAddItemsToWorkOrderMenu = (props: IUseAddToWorkOrderMenu) => {
    const { isDisabledByProjectStatus, statusWorkOrder, filters, statusContract } = props;

    const { id: projectId, orderId } = useParams();
    const { projectData } = useProjectData(projectId, false);
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const { acts: currentWorkOrderActs } = useWorkOrderActs({});

    const [openedModal, setOpenedModal] = useState<Partial<Record<ModalName, boolean>>>({});

    const openModalByName = useCallback((modalName: keyof typeof ModalName) => {
        setOpenedModal({ [modalName]: true });
    }, []);

    const closeModalsHandler = useCallback(() => {
        setOpenedModal({});
    }, []);

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;

    const isStatusWorkOrderOnFormation = statusWorkOrder === WorkOrderStatusEnum.OnFormation;
    const isStatusWorkOrderInProgress = statusWorkOrder === WorkOrderStatusEnum.InProgress;

    const { hasNotActiveOtherActs, hasNotActiveCMRActs } = useMemo(() => {
        const activeActs = currentWorkOrderActs?.filter(
            (act) => act.status === ActKc2StatusEnum.InProgress || act.status === ActKc2StatusEnum.OnCheck,
        );
        const activeOtherActs = activeActs?.filter((act) => act.costGroup === CostGroupEnum.Others);
        const activeCMRActs = activeActs?.filter((act) => act.costGroup === CostGroupEnum.Cmr);

        return {
            hasNotActiveCMRActs: isStatusWorkOrderInProgress && activeCMRActs?.length === 0,
            hasNotActiveOtherActs: isStatusWorkOrderInProgress && activeOtherActs?.length === 0,
        };
    }, [currentWorkOrderActs, isStatusWorkOrderInProgress]);

    const isAddUerDisabled = useMemo(
        () =>
            filters?.level?.value === WorkOrderFilterLevelEnum.TOTALS ||
            !isContractStatusWork ||
            (!isStatusWorkOrderOnFormation && !isStatusWorkOrderInProgress) ||
            hasNotActiveCMRActs ||
            isDisabledByProjectStatus,
        [
            filters?.level?.value,
            hasNotActiveCMRActs,
            isContractStatusWork,
            isDisabledByProjectStatus,
            isStatusWorkOrderInProgress,
            isStatusWorkOrderOnFormation,
        ],
    );

    const isAddCoeffDisabled = useMemo(
        () =>
            filters?.level?.value === WorkOrderFilterLevelEnum.TOTALS ||
            !isContractStatusWork ||
            !isStatusWorkOrderOnFormation ||
            isDisabledByProjectStatus,
        [filters?.level?.value, isContractStatusWork, isDisabledByProjectStatus, isStatusWorkOrderOnFormation],
    );

    const isAddOthersDisabled = useMemo(
        () =>
            filters.level?.value === WorkOrderFilterLevelEnum.TOTALS ||
            !isContractStatusWork ||
            (!isStatusWorkOrderOnFormation && !isStatusWorkOrderInProgress) ||
            hasNotActiveOtherActs ||
            isDisabledByProjectStatus,
        [
            filters.level?.value,
            hasNotActiveOtherActs,
            isContractStatusWork,
            isDisabledByProjectStatus,
            isStatusWorkOrderInProgress,
            isStatusWorkOrderOnFormation,
        ],
    );
    const analyticsData = useMemo(
        () => ({
            projectId,
            contractId: projectData?.contractId,
            orderId: Number(orderId),
            orderStatus: statusWorkOrder,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            orderId,
            projectData?.contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            statusWorkOrder,
        ],
    );

    const menuItems = useMemo(
        () => [
            {
                label: 'Расценку',
                action: () => {
                    openModalByName(ModalName.Uer);

                    if (statusWorkOrder === WorkOrderStatusEnum.OnFormation) {
                        sendGpnAnalyticsEvent({
                            componentId: 'rim_NZ_add price button_pressed',
                            ...analyticsData,
                        });
                    } else if (statusWorkOrder === WorkOrderStatusEnum.InProgress) {
                        sendGpnAnalyticsEvent({
                            componentId: 'rim_act KC_add price button_pressed',
                            ...analyticsData,
                        });
                    }
                },
                disabled: isAddUerDisabled,
                haveTooltip: hasNotActiveCMRActs,
                tooltipName: TooltipComponentName.Uer,
                tooltipMsg: TooltipText.Uer,
            },
            {
                label: 'Коэффициент',
                action: () => {
                    openModalByName(ModalName.Coefficient);
                },
                disabled: isAddCoeffDisabled,
                haveTooltip: isStatusWorkOrderInProgress,
                tooltipName: TooltipComponentName.Coefficient,
                tooltipMsg: TooltipText.Coefficient,
            },
            {
                label: 'Прочие',
                action: () => {
                    openModalByName(ModalName.Other);

                    if (isStatusWorkOrderOnFormation) {
                        sendGpnAnalyticsEvent({
                            componentId: 'rim_NZ_add other button_pressed',
                            ...analyticsData,
                        });
                    }

                    if (isStatusWorkOrderInProgress) {
                        sendGpnAnalyticsEvent({
                            componentId: 'rim_act KC_add other button_pressed',
                            ...analyticsData,
                        });
                    }
                },
                disabled: isAddOthersDisabled,
                haveTooltip: hasNotActiveOtherActs,
                tooltipName: TooltipComponentName.Other,
                tooltipMsg: TooltipText.Other,
            },
        ],
        [
            analyticsData,
            hasNotActiveCMRActs,
            hasNotActiveOtherActs,
            isAddCoeffDisabled,
            isAddOthersDisabled,
            isAddUerDisabled,
            isStatusWorkOrderInProgress,
            isStatusWorkOrderOnFormation,
            openModalByName,
            sendGpnAnalyticsEvent,
            statusWorkOrder,
        ],
    );

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    return useMemo(
        () => ({
            addToWorkOrderMenuItems: menuItems,
            openedModal,
            closeModalsHandler,
            getMenuItemAttributes,
            onMenuMouseMove,
            tooltipPosition,
            activeTooltipMsg,
            onMenuMouseLeave,
        }),
        [
            activeTooltipMsg,
            closeModalsHandler,
            getMenuItemAttributes,
            menuItems,
            onMenuMouseLeave,
            onMenuMouseMove,
            openedModal,
            tooltipPosition,
        ],
    );
};
